/**
 * WP8 · BK-Profil — Options
 *
 * Wiederverwendet die geteilten PB-Options (profile_options-Loader,
 * Fallbacks, HARDCODED-Listen) aus `pflege/profile/options` und ergänzt
 * NUR die BK-spezifischen Hardcoded-Listen.
 *
 * qualifikation-Werte stammen 1:1 aus CRM 1.0
 * (`crm1.0/sh24-frontend/src/screens/profile/ProfileData.js`,
 * getQualificationLabel + Selectbox-Props): slug → Label.
 */
import type { SelectOption } from '../../pflege/profile/controls';
import { HARDCODED as SHARED_HARDCODED } from '../../pflege/profile/options';

// Re-Export der geteilten Options-Helfer — BK-Sections nutzen dieselben
// DB-Options (bk_wohnen, bk_aktivitaeten, bk_anrede, bk_deutsch,
// bk_fuehrerschein, familienstand, raucher, …) und HARDCODED-Listen
// (anrede, krankenkasse_typ, badge, impfstatus, status, konto, land).
export { FALLBACK_OPTS, fieldOptions, labelOptions, HARDCODED } from '../../pflege/profile/options';

/* ─── BK-Status (users.status) ─────────────────────────────────────────── */

/** users.status „Nicht erreichbar" — manuell gesetzt, vom CareStateWorker
 * nicht überschrieben, im Matching ausgeblendet (Backend: User::STATUS_NICHT_ERREICHBAR). */
export const BK_STATUS_NICHT_ERREICHBAR = 4;

/**
 * BK-eigene Status-Liste — Bedeutung wie der CareStateWorker sie schreibt
 * und wie die BK-Liste sie anzeigt (BetreuungPage/constants.ts,
 * ARBEITSSTATUS_LABEL_KEY): 0 Verfügbar, 1 In Betreuung, 2 Pausierend,
 * 3 Gekündigt, 4 Nicht erreichbar. Die geteilte PB-Liste (1 Aktiv,
 * 2 Ausstehend, …) passt für BKs nicht — „Ausstehend" gibt es hier nicht.
 */
export const BK_STATUS_LABEL_KEYS: Record<string, string> = {
  '0': 'betreuung.list.arbeitsstatus.verfuegbar',
  '1': 'betreuung.list.arbeitsstatus.in_betreuung',
  '2': 'betreuung.list.arbeitsstatus.pausierend',
  '3': 'betreuung.list.arbeitsstatus.gekuendigt',
  [String(BK_STATUS_NICHT_ERREICHBAR)]: 'betreuung.list.arbeitsstatus.nicht_erreichbar',
};

/** Übersetztes Label eines BK-Status; Altwerte außerhalb der Liste (z.B. 9)
 * über die geteilte Liste, sonst der Rohwert; leer → null. */
export function bkStatusLabel(value: unknown, tr: (key: string) => string): string | null {
  if (value == null || value === '') return null;
  const v = String(value);
  const key = BK_STATUS_LABEL_KEYS[v];
  if (key) return tr(key);
  return SHARED_HARDCODED.status.find(o => o.value === v)?.label ?? v;
}

/**
 * Manuell wählbare BK-Stati: Verfügbar (0), Gekündigt (3), Nicht erreichbar (4).
 * 3 und 4 lässt der CareStateWorker stehen (BK fällt aus dem Matching); 0 gibt
 * den Status an die Automatik zurück. In Betreuung (1) / Pausierend (2) rechnet
 * allein der Worker — sie erscheinen nur als aktueller Wert.
 */
export const BK_STATUS_MANUAL = ['0', '3', String(BK_STATUS_NICHT_ERREICHBAR)];

/** Select-Optionen für den BK-Status; ein berechneter oder Altwert bleibt sichtbar. */
export function bkStatusOptions(tr: (key: string) => string, currentValue?: unknown): SelectOption[] {
  // Im Dropdown heißt Status 0 „Aktiviert" (gespeicherter Wert bleibt 0; Liste und Seitenleiste sagen weiter „Verfügbar").
  const opts = BK_STATUS_MANUAL.map(v => ({ value: v, label: tr(v === '0' ? 'betreuung.form.status_aktiviert' : BK_STATUS_LABEL_KEYS[v]) }));
  const cur = currentValue == null ? '' : String(currentValue);
  if (cur !== '' && !BK_STATUS_MANUAL.includes(cur)) {
    opts.push({ value: cur, label: bkStatusLabel(cur, tr) ?? cur });
  }
  return opts;
}

/* ─── BK-spezifische Hardcoded-Optionen ────────────────────────────────── */
export const BK_HARDCODED = {
  /** profiles.qualifikation — einziges explizit BK-spezifisches Profilfeld. */
  qualifikation: [
    { value: 'hilfskraft',       label: 'Hilfskraft' },
    { value: 'ausbildung',       label: 'In Ausbildung' },
    { value: 'alltagsbegleiter', label: 'Geprüfte(r) Alltagsbegleiter*in' },
    { value: 'pflegefachkraft',  label: 'Examinierte Pflegefachkraft' },
  ] as SelectOption[],

  /**
   * prices_bk.vertrag — BK-Vertragsarten, 1:1 aus CRM 1.0
   * (`bkContracts` in scripts/Functions.js).
   */
  vertrag: [
    { value: 'avp', label: 'AV Polnisch' },
    { value: 'avd', label: 'AV Deutsch' },
    { value: 'avk', label: 'AV Kombi' },
    { value: 'ep',  label: 'Externer Partner' },
  ] as SelectOption[],
};
